import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/compliancePermissions';
import {
  useComplianceRecheckTasks,
  type ComplianceRecheckTaskDto,
} from '@/hooks/useCompliance';
import {
  COMPLIANCE_RECHECK_STATUS_LABELS,
  formatEnumLabel,
} from '@/lib/enumLabels';

const COMPLIANCE_RECHECK_STATUSES: readonly ComplianceRecheckTaskDto['status'][] = [
  'pending',
  'in_progress',
  'resolved',
  'cancelled',
];

const OPEN_STATUSES = new Set<ComplianceRecheckTaskDto['status']>(['pending', 'in_progress']);

function fmtDateTime(value?: string): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

function statusTone(status: ComplianceRecheckTaskDto['status']): string {
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-700';
  if (status === 'cancelled') return 'bg-slate-100 text-slate-600';
  if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
}

export default function RecheckQueuePage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLIANCE_PERMISSIONS.RECHECK_TASK_READ);
  const canAssign = hasPermission(permissions, COMPLIANCE_PERMISSIONS.RECHECK_TASK_ASSIGN);
  const canResolve = hasPermission(permissions, COMPLIANCE_PERMISSIONS.RECHECK_TASK_RESOLVE);
  const canCancel = hasPermission(permissions, COMPLIANCE_PERMISSIONS.RECHECK_TASK_CANCEL);
  const { tasks, loading, error, fetchTasks, assignTask, resolveTask, cancelTask } = useComplianceRecheckTasks();

  const [statusFilter, setStatusFilter] = useState<ComplianceRecheckTaskDto['status'] | ''>('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [draftAssignee, setDraftAssignee] = useState('');
  const [draftDueAt, setDraftDueAt] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  useEffect(() => {
    if (!canRead) {
      return;
    }
    void fetchTasks({
      status: statusFilter || undefined,
      assigneeId: assigneeFilter || undefined,
    });
  }, [canRead, statusFilter, assigneeFilter, fetchTasks]);

  if (!canRead) {
    return <AccessDenied resource="复核队列" />;
  }

  async function handleAssign(item: ComplianceRecheckTaskDto) {
    if (!draftAssignee) {
      return;
    }
    const result = await assignTask(item.id, {
      expectedVersion: item.version,
      assigneeId: draftAssignee,
      dueAt: draftDueAt ? new Date(draftDueAt).toISOString() : undefined,
    });
    if (result) {
      setDraftAssignee('');
      setDraftDueAt('');
      void fetchTasks({
        status: statusFilter || undefined,
        assigneeId: assigneeFilter || undefined,
      });
    }
  }

  async function handleResolve(item: ComplianceRecheckTaskDto) {
    if (!draftNotes) {
      return;
    }
    const result = await resolveTask(item.id, {
      expectedVersion: item.version,
      resolutionNotes: draftNotes,
    });
    if (result) {
      setDraftNotes('');
      void fetchTasks({
        status: statusFilter || undefined,
        assigneeId: assigneeFilter || undefined,
      });
    }
  }

  async function handleCancel(item: ComplianceRecheckTaskDto) {
    if (!draftNotes) {
      return;
    }
    const result = await cancelTask(item.id, {
      expectedVersion: item.version,
      resolutionNotes: draftNotes,
    });
    if (result) {
      setDraftNotes('');
      void fetchTasks({
        status: statusFilter || undefined,
        assigneeId: assigneeFilter || undefined,
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">复核队列</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            项目状态降级后对已签客户自动生成的复核任务，要求分派、处理、闭环可追溯。
          </p>
        </div>
        <div className="flex gap-3">
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="按 assigneeId 过滤"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ComplianceRecheckTaskDto['status'] | '')}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部状态</option>
            {COMPLIANCE_RECHECK_STATUSES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, COMPLIANCE_RECHECK_STATUS_LABELS, '未知状态')}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-3">
        <input
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          placeholder="分派 assigneeId"
          value={draftAssignee}
          onChange={(e) => setDraftAssignee(e.target.value)}
        />
        <input
          type="datetime-local"
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          value={draftDueAt}
          onChange={(e) => setDraftDueAt(e.target.value)}
        />
        <textarea
          className="min-h-20 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-3"
          placeholder="处理说明 / 取消原因"
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
        />
      </div>

      {error && <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>}

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">项目</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">客户 / 项目单</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">优先级</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">负责人</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">触发时间</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">加载中…</td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">暂无复核任务</td>
              </tr>
            ) : (
              tasks.map((item) => (
                <tr key={item.id} className="border-t align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{item.projectCode}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{item.triggerReason}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">
                    <div>customer={item.customerId}</div>
                    <div>orderProject={item.orderProjectId || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${statusTone(item.status)}`}>
                      {formatEnumLabel(item.status, COMPLIANCE_RECHECK_STATUS_LABELS, '未知状态')}
                    </span>
                  </td>
                  <td className="px-4 py-3">{item.priority}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.assigneeId || '未分派'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <div>{fmtDateTime(item.triggerEventAt)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">due: {fmtDateTime(item.dueAt)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {canAssign && item.status === 'pending' && (
                        <button
                          onClick={() => { void handleAssign(item); }}
                          className="rounded bg-blue-700 px-2 py-1 text-xs text-white hover:bg-blue-600"
                        >
                          分派
                        </button>
                      )}
                      {canResolve && OPEN_STATUSES.has(item.status) && (
                        <button
                          onClick={() => { void handleResolve(item); }}
                          className="rounded bg-emerald-700 px-2 py-1 text-xs text-white hover:bg-emerald-600"
                        >
                          结案
                        </button>
                      )}
                      {canCancel && OPEN_STATUSES.has(item.status) && (
                        <button
                          onClick={() => { void handleCancel(item); }}
                          className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300"
                        >
                          取消
                        </button>
                      )}
                    </div>
                    {(item.resolutionNotes || item.resolvedBy) && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        {item.resolvedBy || 'system'} · {item.resolutionNotes || '无处理说明'}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
